import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { GableFeedbackChip } from '../../../services/gable/gable.service';
import { SimpleBadgeComponent } from '../../v2-layout/shared/simple-badge/simple-badge.component';

interface ChipOption {
    id: GableFeedbackChip;
    label: string;
    /** Placeholder used when the user opens the Send-feedback panel for this chip. */
    detailPlaceholder: string;
}

/**
 * Inline reason-chips panel that animates in under an assistant message
 * when the user clicks thumbs-down: chips → Send-feedback panel.
 *
 * Owns no async state of its own — the parent (gable-drawer-host) handles
 * persisting the chip telemetry and dismissal. This component is purely
 * the visual + interaction layer.
 *
 * Animation: opacity + max-height transition managed via the `is-open`
 * class. Width/padding stay constant so we don't reflow neighboring
 * bubbles when we open or close.
 */
@Component({
    selector: 'app-gable-feedback-panel',
    templateUrl: './gable-feedback-panel.component.html',
    styleUrls: ['./gable-feedback-panel.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        SimpleBadgeComponent,
    ],
})
export class GableFeedbackPanelComponent {
    /** True when thumbs-down is selected for this message. Drives the slide-in animation. */
    @Input() open = false;

    @Output() readonly chipSelected = new EventEmitter<{ chip: GableFeedbackChip; detail?: string }>();
    /** User dismissed the panel via Skip. */
    @Output() readonly skipped = new EventEmitter<void>();

    readonly chips: ReadonlyArray<ChipOption> = [
        { id: 'wrong_data', label: 'Wrong data', detailPlaceholder: 'What number or fact was off?' },
        { id: 'missed_nuance', label: 'Missed nuance', detailPlaceholder: 'What did Gable miss?' },
        { id: 'wrong_tone', label: 'Wrong tone', detailPlaceholder: 'What felt off about the phrasing or tone?' },
        { id: 'off_topic', label: 'Off-topic', detailPlaceholder: 'What did you expect Gable to answer instead?' },
        { id: 'other', label: 'Other', detailPlaceholder: 'Tell us what was wrong…' },
    ];

    selectedChip: GableFeedbackChip | null = null;
    detailText = '';

    constructor(private readonly _cdr: ChangeDetectorRef) {}

    get showSendPanel(): boolean {
        return this.selectedChip !== null;
    }

    get currentDetailPlaceholder(): string {
        return this.chips.find((c) => c.id === this.selectedChip)?.detailPlaceholder ?? '';
    }

    onChipClick(chip: GableFeedbackChip): void {
        // Re-clicking the selected chip clears it (single-select toggle).
        if (this.selectedChip === chip) {
            this.selectedChip = null;
            this.detailText = '';
        } else {
            this.selectedChip = chip;
            this.detailText = '';
        }
        this._cdr.markForCheck();
    }

    onSendClick(): void {
        if (!this.selectedChip) { return; }
        this.chipSelected.emit({
            chip: this.selectedChip,
            detail: this.detailText.trim() === '' ? undefined : this.detailText.trim(),
        });
    }

    /**
     * Skip still records the chip selection (the chip alone is useful
     * telemetry) but without the free-text detail. The host marks the
     * interaction as feedback-sent and swaps the panel for the green
     * confirmation, same as Send feedback does.
     */
    onSkipClick(): void {
        if (this.selectedChip) {
            this.chipSelected.emit({ chip: this.selectedChip });
        }
        this.skipped.emit();
    }

    /** Reset internal state when the panel closes (e.g. parent collapsed it). */
    reset(): void {
        this.selectedChip = null;
        this.detailText = '';
        this._cdr.markForCheck();
    }
}
